import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, 
  AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import {
  getDashboardOverview,
  getDashboardTrends,
  getCategoryBreakdown,
  getPriorityBreakdown,
  getPerformanceMetrics,
  getRecentActivity
} from '../../api/dashboardApi';
import { getMyTickets } from '../../api/ticketApi';
import StatsCard from '../../components/Dashboard/StatsCard';
import TicketList from '../../components/Tickets/TicketList';
import Loading from '../../components/Common/Loading';
import ErrorMessage from '../../components/Common/ErrorMessage';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import './AgentDashboard.css';

const COLORS = ['#667eea', '#4caf50', '#ff9800', '#f44336', '#2196f3', '#9c27b0', '#00bcd4'];

export default function AgentDashboard() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [activeView, setActiveView] = useState('overview');

  // Fetch data only once on mount and when statusFilter changes
  useEffect(() => {
    fetchAllData();
  }, [statusFilter]); // Remove fetchAllData from dependencies to prevent infinite loop

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        overviewRes,
        trendsRes,
        categoriesRes,
        prioritiesRes,
        performanceRes,
        activityRes,
        ticketsRes
      ] = await Promise.all([
        getDashboardOverview(),
        getDashboardTrends(7),
        getCategoryBreakdown(),
        getPriorityBreakdown(),
        getPerformanceMetrics(),
        getRecentActivity(10),
        getMyTickets(statusFilter)
      ]);

      setOverview(overviewRes.data);
      setTrends(trendsRes.data);
      setCategories(categoriesRes.data);
      setPriorities(prioritiesRes.data);
      setPerformance(performanceRes.data);
      setRecentActivity(activityRes.data);
      setTickets(ticketsRes.tickets);

    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.response?.data?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Calculate additional metrics
  const calculateMetrics = () => {
    if (!overview) return {};

    const total = overview.total || 0;
    const closed = overview.closed || 0;
    const closedToday = overview.closed_today || 0;
    const open = overview.open || 0;
    const inProgress = overview.in_progress || 0;

    const resolutionRate = total > 0 ? ((closed / total) * 100).toFixed(1) : 0;
    const activeRate = total > 0 ? (((open + inProgress) / total) * 100).toFixed(1) : 0;
    const todayProductivity = closedToday;

    return {
      resolutionRate,
      activeRate,
      todayProductivity,
      totalActive: open + inProgress
    };
  };

  const metrics = calculateMetrics();

  if (loading) {
    return <Loading message="Loading dashboard..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={fetchAllData} />;
  }

  return (
    <div className="agent-dashboard">
      {/* Header */}
      <header className="dashboard__header">
        <div>
          <h1 className="dashboard__title">My Dashboard</h1>
          <p className="dashboard__subtitle">Welcome back! Here's your performance overview</p>
        </div>
        <div className="dashboard__header-actions">
          <button className="dashboard__refresh-btn" onClick={fetchAllData}>
            🔄 Refresh
          </button>
        </div>
      </header>

      {/* View Tabs */}
      <div className="dashboard__tabs">
        <button 
          className={`dashboard__tab ${activeView === 'overview' ? 'dashboard__tab--active' : ''}`}
          onClick={() => setActiveView('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`dashboard__tab ${activeView === 'analytics' ? 'dashboard__tab--active' : ''}`}
          onClick={() => setActiveView('analytics')}
        >
          📈 Analytics
        </button>
        <button 
          className={`dashboard__tab ${activeView === 'tickets' ? 'dashboard__tab--active' : ''}`}
          onClick={() => setActiveView('tickets')}
        >
          🎫 My Tickets
        </button>
      </div>

      {/* OVERVIEW TAB */}
      {activeView === 'overview' && (
        <>
          {/* Stats Cards Row 1 */}
          <div className="dashboard__stats-grid">
            <StatsCard title="Total Tickets" value={overview?.total || 0} icon="🎫" color="#667eea" />
            <StatsCard title="Open Tickets" value={overview?.open || 0} icon="📂" color="#ff9800" />
            <StatsCard title="In Progress" value={overview?.in_progress || 0} icon="⚙️" color="#2196f3" />
            <StatsCard title="Closed Today" value={overview?.closed_today || 0} icon="✅" color="#4caf50" />
          </div>

          {/* Stats Cards Row 2 */}
          <div className="dashboard__stats-grid">
            <StatsCard title="Total Closed" value={overview?.closed || 0} icon="🏆" color="#9c27b0" />
            <StatsCard title="Pending" value={overview?.pending || 0} icon="⏳" color="#795548" />
            <StatsCard title="Created Today" value={overview?.created_today || 0} icon="🆕" color="#00bcd4" />
            <StatsCard title="Active Tickets" value={metrics.totalActive || 0} icon="🔥" color="#f44336" />
          </div>

          {/* Performance Overview */}
          <div className="dashboard__performance-overview">
            <div className="performance-overview__card">
              <h3>📊 Resolution Rate</h3>
              <div className="circular-progress">
                <CircularProgressbar
                  value={metrics.resolutionRate}
                  text={`${metrics.resolutionRate}%`}
                  styles={buildStyles({ pathColor: '#4caf50', textColor: '#333', trailColor: '#e0e0e0' })}
                />
              </div>
              <p className="performance-overview__label">{overview?.closed || 0} of {overview?.total || 0} resolved</p>
            </div>

            <div className="performance-overview__card">
              <h3>⚡ Active Workload</h3>
              <div className="circular-progress">
                <CircularProgressbar
                  value={metrics.activeRate}
                  text={`${metrics.activeRate}%`}
                  styles={buildStyles({ pathColor: '#ff9800', textColor: '#333', trailColor: '#e0e0e0' })}
                />
              </div>
              <p className="performance-overview__label">{metrics.totalActive} tickets in progress</p>
            </div>

            <div className="performance-overview__card">
              <h3>🎯 Today's Goal</h3>
              <div className="circular-progress">
                <CircularProgressbar
                  value={Math.min(((overview?.closed_today || 0) / 5) * 100, 100)}
                  text={`${overview?.closed_today || 0}/5`}
                  styles={buildStyles({ pathColor: '#2196f3', textColor: '#333', trailColor: '#e0e0e0' })}
                />
              </div>
              <p className="performance-overview__label">Target: 5 tickets per day</p>
            </div>

            {performance && (
              <div className="performance-overview__card">
                <h3>⏱️ Avg Response Time</h3>
                <div className="performance-metric-large">
                  <span className="performance-metric-large__value">{performance.avg_response_time_minutes?.toFixed(0) || 0}</span>
                  <span className="performance-metric-large__unit">minutes</span>
                </div>
                <p className="performance-overview__label">Based on {performance.tickets_analyzed || 0} tickets</p>
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="dashboard__recent-activity">
            <div className="section-header">
              <h3>🕐 Recent Activity</h3>
              <span className="section-badge">{recentActivity.length} items</span>
            </div>
            <div className="activity-list">
              {recentActivity.length === 0 ? (
                <p>No recent activity</p>
              ) : (
                recentActivity.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="activity-item" onClick={() => navigate(`/ticket/${activity.id}`)}>
                    <div className="activity-item__icon" style={{ 
                      backgroundColor: activity.priority === 'Urgent' ? '#f44336' : activity.priority === 'High' ? '#ff9800' : '#4caf50'
                    }}>
                      {activity.priority === 'Urgent' ? '🔥' : activity.priority === 'High' ? '⚠️' : '📋'}
                    </div>
                    <div className="activity-item__content">
                      <span className="activity-item__title">{activity.ticket_number}</span>
                      <span className="activity-item__category">{activity.category}</span>
                    </div>
                    <div className="activity-item__status">
                      <span className={`status-badge status-badge--${activity.status.toLowerCase().replace(' ', '-')}`}>
                        {activity.status}
                      </span>
                      <span className="activity-item__time">{activity.updated_at}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {/* ANALYTICS TAB */}
      {activeView === 'analytics' && (
        <div className="dashboard__analytics">
          <div className="dashboard__chart-card dashboard__chart-card--full">
            <div className="dashboard__chart-header"><h3>📈 Ticket Trends (Last 7 Days)</h3></div>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={trends?.created || []}>
                <defs>
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#667eea" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#667eea" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="count" stroke="#667eea" fillOpacity={1} fill="url(#colorCount)" name="Tickets Created" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="dashboard__charts-row">
            <div className="dashboard__chart-card">
              <div className="dashboard__chart-header"><h3>📊 By Category</h3></div>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={categories} dataKey="count" nameKey="category" cx="50%" cy="50%" outerRadius={80} label>
                    {categories.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="dashboard__chart-card">
              <div className="dashboard__chart-header"><h3>🔥 By Priority</h3></div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={priorities}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="priority" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#667eea" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* TICKETS TAB */}
      {activeView === 'tickets' && (
        <div className="dashboard__tickets-section">
          <TicketList tickets={tickets} loading={false} onStatusFilter={setStatusFilter} />
        </div>
      )}
    </div>
  );
}
